// ===================================
// CONFIGURAZIONE
// ===================================

const CONFIG = {
    API_URL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
    FEEDBACK_EMAIL: import.meta.env.VITE_FEEDBACK_EMAIL || 'feedback@esempio.com',
    COOKIE_CONSENT_KEY: 'scam_detector_cookie_consent',
    DISCLAIMER_SEEN_KEY: 'scam_detector_disclaimer_seen',
};

// ===================================
// GESTIONE COOKIE
// ===================================

class CookieManager {
    constructor() {
        this.banner = document.getElementById('cookie-banner');
        this.acceptBtn = document.getElementById('cookie-accept');
        this.settingsBtn = document.getElementById('cookie-settings');
        
        this.init();
    }
    
    init() {
        // Mostra banner solo se non è già stato accettato
        if (!this.hasConsent()) {
            this.showBanner();
        }
        
        this.acceptBtn.addEventListener('click', () => this.acceptCookies());
        this.settingsBtn.addEventListener('click', () => this.showCookieDetails());
    }
    
    hasConsent() {
        return localStorage.getItem(CONFIG.COOKIE_CONSENT_KEY) === 'true';
    }
    
    showBanner() {
        this.banner.classList.remove('hidden');
    }
    
    hideBanner() {
        this.banner.style.animation = 'slideDown 0.3s ease-out';
        setTimeout(() => {
            this.banner.classList.add('hidden');
        }, 300);
    }
    
    acceptCookies() {
        localStorage.setItem(CONFIG.COOKIE_CONSENT_KEY, 'true');
        this.hideBanner();
    }
    
    showCookieDetails() {
        const modal = document.getElementById('cookie-details-modal');
        const closeBtn = document.getElementById('cookie-details-close');
        modal.classList.remove('hidden');

        const close = () => {
            modal.classList.add('hidden');
            closeBtn.removeEventListener('click', close);
            modal.removeEventListener('click', onBackdrop);
        };
        const onBackdrop = (e) => { if (e.target === modal) close(); };

        closeBtn.addEventListener('click', close);
        modal.addEventListener('click', onBackdrop);
    }
}

// ===================================
// GESTIONE DISCLAIMER
// ===================================

class DisclaimerManager {
    constructor() {
        this.modal = document.getElementById('disclaimer-modal');
        this.closeBtn = document.getElementById('disclaimer-close');
        this.checkbox = document.getElementById('disclaimer-checkbox');
        
        this.init();
    }
    
    init() {
        // Mostra disclaimer solo al primo accesso
        if (!this.hasSeenDisclaimer()) {
            this.showModal();
        }
        
        this.closeBtn.addEventListener('click', () => this.closeModal());
        
        // Chiudi con ESC
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !this.modal.classList.contains('hidden')) {
                this.closeModal();
            }
        });
    }
    
    hasSeenDisclaimer() {
        return localStorage.getItem(CONFIG.DISCLAIMER_SEEN_KEY) === 'true';
    }
    
    showModal() {
        this.modal.classList.remove('hidden');
        // Focus sul bottone di chiusura per accessibilità
        setTimeout(() => this.closeBtn.focus(), 100);
    }
    
    closeModal() {
        // Salva preferenza se checkbox selezionata
        if (this.checkbox.checked) {
            localStorage.setItem(CONFIG.DISCLAIMER_SEEN_KEY, 'true');
        }
        
        this.modal.classList.add('hidden');
    }
}

// ===================================
// ANALISI URL
// ===================================

class URLAnalyzer {
    constructor() {
        this.form = document.getElementById('search-form');
        this.input = document.getElementById('url-input');
        this.submitBtn = document.getElementById('verify-btn');
        this.loadingEl = document.getElementById('loading');
        this.resultsEl = document.getElementById('results');
        
        this.init();
    }
    
    init() {
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.analyzeURL();
        });
    }
    
    async analyzeURL() {
        const url = this.input.value.trim();
        
        if (!url) {
            this.showError('Per favore inserisci un indirizzo web.');
            return;
        }
        
        // Mostra loading
        this.showLoading();
        this.hideResults();
        this.submitBtn.disabled = true;
        
        try {
            const response = await fetch(`${CONFIG.API_URL}/analyze`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ url }),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Errore durante l\'analisi');
            }
            
            this.hideLoading();
            this.showResults(data);
            
        } catch (error) {
            this.hideLoading();
            this.showError(error.message);
        } finally {
            this.submitBtn.disabled = false;
        }
    }
    
    showLoading() {
        this.loadingEl.classList.remove('hidden');
        // Scroll alla sezione loading
        this.loadingEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    hideLoading() {
        this.loadingEl.classList.add('hidden');
    }
    
    showResults(data) {
        const risk = data.riskAssessment;
        const html = this.buildResultsHTML(data, risk);
        
        this.resultsEl.innerHTML = html;
        this.resultsEl.classList.remove('hidden');
        
        // Scroll ai risultati
        this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    hideResults() {
        this.resultsEl.classList.add('hidden');
    }
    
    buildResultsHTML(data, risk) {
        const riskColor = this.getRiskColor(risk.level);
        const riskIcon = this.getRiskIcon(risk.level);
        
        // Genera checklist sicurezza
        const securityChecklist = this.buildSecurityChecklist(data, risk);
        
        // Genera messaggio contestuale
        const contextualMessage = this.buildContextualMessage(data, risk);
        
        return `
            <div class="result-card" style="border-left: 6px solid ${riskColor};">
                <div class="result-header">
                    <div class="result-icon" style="font-size: 4rem;" aria-hidden="true">${riskIcon}</div>
                    <h2 class="result-title">${risk.label}</h2>
                    <div class="result-url">
                        <strong>Sito analizzato:</strong> ${this.escapeHTML(data.url)}
                    </div>
                </div>
                
                <!-- Checklist Sicurezza -->
                ${securityChecklist}
                
                <!-- Messaggio Contestuale -->
                ${contextualMessage}
                
                <!-- Score (nascosto di default, espandibile) -->
                <details class="score-details">
                    <summary class="score-summary">📊 Punteggio Tecnico</summary>
                    <div class="result-score">
                        <div class="score-label">Punteggio di Rischio (0-100)</div>
                        <div class="score-bar">
                            <div class="score-fill" style="width: ${risk.score}%; background: ${riskColor};" role="progressbar" aria-valuenow="${risk.score}" aria-valuemin="0" aria-valuemax="100">
                            </div>
                        </div>
                        <div class="score-value-below">${risk.score}/100</div>
                        <p class="score-explanation">Questo punteggio è calcolato da ${risk.flags.length} segnali rilevati.</p>
                    </div>
                </details>
                
                <!-- Dettagli Tecnici (espandibile) -->
                ${risk.flags.length > 0 ? this.buildFlagsHTML(risk.flags) : ''}
                
                ${data.recommendations.length > 0 ? this.buildRecommendationsHTML(data.recommendations) : ''}
                
                <div class="result-footer">
                    <p class="result-timestamp">Analisi effettuata il ${new Date(data.analyzedAt).toLocaleString('it-IT')}</p>
                    <p class="result-disclaimer">
                        ⚠️ Questo risultato è indicativo. Usa sempre prudenza quando navighi online.
                    </p>
                </div>
            </div>
        `;
    }
    
    buildSecurityChecklist(data, risk) {
        const checks = [];
        const serviceErrors = []; // Traccia errori servizi
        
        // 1. Malware Scan
        const vtData = data.results?.virustotal?.data;
        const vtError = data.results?.virustotal?.error;
        
        if (vtError) {
            checks.push({
                status: 'error',
                label: 'Malware Scan',
                detail: 'Errore servizio VirusTotal',
                errorDetail: vtError.message || 'Servizio non disponibile'
            });
            serviceErrors.push({
                service: 'VirusTotal',
                error: vtError.message || 'Servizio temporaneamente non disponibile'
            });
        } else if (vtData && vtData.status === 'pending') {
            // URL sottomesso ma risultati non ancora pronti
            checks.push({
                status: 'unknown',
                label: 'Malware Scan',
                detail: 'Analisi in corso - riprova tra qualche minuto'
            });
        } else if (vtData && vtData.total !== undefined) {
            checks.push({
                status: vtData.positives === 0 ? 'pass' : 'fail',
                label: 'Malware Scan',
                detail: vtData.positives === 0 
                    ? '0 minacce rilevate' 
                    : `${vtData.positives}/${vtData.total} antivirus hanno rilevato minacce`
            });
        } else {
            checks.push({
                status: 'unknown',
                label: 'Malware Scan',
                detail: 'Sito non presente in VirusTotal'
            });
        }
        
        // 2. Database Phishing
        const gsbData = data.results?.safebrowsing?.data;
        const gsbError = data.results?.safebrowsing?.error;
        
        if (gsbError) {
            checks.push({
                status: 'error',
                label: 'Database Phishing',
                detail: 'Errore servizio Google Safe Browsing',
                errorDetail: gsbError.message || 'Servizio non disponibile'
            });
            serviceErrors.push({
                service: 'Google Safe Browsing',
                error: this.parseAPIError(gsbError.message) || 'Servizio temporaneamente non disponibile'
            });
        } else if (gsbData && gsbData.checked === true) {
            checks.push({
                status: gsbData.isSafe === true ? 'pass' : 'fail',
                label: 'Database Phishing',
                detail: gsbData.isSafe === true ? 'Nessuna segnalazione' : 'Sito segnalato per phishing'
            });
        } else {
            checks.push({
                status: 'unknown',
                label: 'Database Phishing',
                detail: 'Controllo non disponibile'
            });
        }
        
        // 3. Certificato SSL
        const sslData = data.results?.ssl?.data;
        const sslError = data.results?.ssl?.error;
        
        if (sslError) {
            checks.push({
                status: 'error',
                label: 'Certificato SSL',
                detail: 'Errore verifica SSL',
                errorDetail: sslError.message
            });
            serviceErrors.push({
                service: 'SSL Check',
                error: 'Impossibile verificare certificato'
            });
        } else if (sslData && sslData.hasSSL !== undefined) {
            const isGood = sslData.hasSSL && sslData.valid && !sslData.suspicious;
            checks.push({
                status: isGood ? 'pass' : (sslData.hasSSL ? 'warn' : 'fail'),
                label: 'Certificato SSL',
                detail: !sslData.hasSSL
                           ? (sslData.suspicionReasons?.[0] || 'Sito non usa HTTPS')
                           : !sslData.valid ? 'Certificato non valido o scaduto'
                           : sslData.suspicious ? 'Certificato valido ma con avvisi'
                           : 'Valido e sicuro'
            });
        } else {
            checks.push({
                status: 'unknown',
                label: 'Certificato SSL',
                detail: 'Controllo non eseguito'
            });
        }
        
        // 4. Struttura URL
        const hasUrlIssues = risk.flags.some(f => 
            ['suspicious_url_structure', 'suspicious_hash', 'suspicious_parameters', 'hosting_service'].includes(f.category)
        );
        checks.push({
            status: hasUrlIssues ? 'fail' : 'pass',
            label: 'Struttura URL',
            detail: hasUrlIssues ? 'Pattern sospetti rilevati' : 'Struttura standard'
        });
        
        // 5. Pattern Analisi
        const patternIssues = risk.flags.filter(f => f.category === 'url_pattern' || f.category === 'crypto_keywords' || f.category === 'brand_typosquatting');
        checks.push({
            status: patternIssues.length > 2 ? 'fail' : (patternIssues.length > 0 ? 'warn' : 'pass'),
            label: 'Pattern Analisi',
            detail: patternIssues.length === 0 
                ? 'Nessun pattern sospetto' 
                : `${patternIssues.length} pattern rilevat${patternIssues.length === 1 ? 'o' : 'i'}`
        });
        
        // 6. Location IP (GeoIP)
        const geoipData = data.results?.geoip?.data;
        const geoipError = data.results?.geoip?.error;
        
        if (geoipError) {
            checks.push({
                status: 'error',
                label: 'Location IP',
                detail: 'Errore servizio geolocalizzazione',
                errorDetail: geoipError.message
            });
            serviceErrors.push({
                service: 'GeoIP',
                error: 'Servizio di geolocalizzazione non disponibile'
            });
        } else if (geoipData && geoipData.cannotResolve) {
            // IP non risolvibile - warning ma non errore critico
            checks.push({
                status: 'warn',
                label: 'Location IP',
                detail: 'Dominio non raggiungibile o DNS non configurato'
            });
        } else if (geoipData && geoipData.country) {
            const hasIssues = geoipData.suspicious && geoipData.suspicionReasons?.length > 0;
            checks.push({
                status: hasIssues ? 'warn' : 'pass',
                label: 'Location IP',
                detail: hasIssues 
                    ? geoipData.suspicionReasons[0] 
                    : `Ospitato in ${geoipData.country}`
            });
        } else {
            checks.push({
                status: 'unknown',
                label: 'Location IP',
                detail: 'Informazioni non disponibili'
            });
        }
        
        // 7. Età Dominio
        // Preferisce whois se ha registered=true, altrimenti fallback su whoisxml
        const whoisRaw = data.results?.whois?.data;
        const whoisxmlRaw = data.results?.whoisxml?.data;
        const whoisData = (whoisRaw?.registered === true) ? whoisRaw
                        : (whoisxmlRaw?.registered === true) ? whoisxmlRaw
                        : whoisRaw || whoisxmlRaw;
        const whoisError = data.results?.whois?.error;
        
        if (whoisError && whoisError.message?.includes('timeout')) {
            checks.push({
                status: 'error',
                label: 'Età Dominio',
                detail: 'Timeout servizio WHOIS',
                errorDetail: 'Servizio WHOIS non risponde'
            });
            serviceErrors.push({
                service: 'WHOIS',
                error: 'Timeout del server - riprova tra qualche minuto'
            });
        } else if (whoisData && whoisData.registered === true && whoisData.ageDays !== null && whoisData.ageDays !== undefined) {
            const age = whoisData.ageDays;
            checks.push({
                status: age > 365 ? 'pass' : (age > 90 ? 'warn' : 'fail'),
                label: 'Età Dominio',
                detail: age > 365 ? `${Math.floor(age/365)} ann${Math.floor(age/365) === 1 ? 'o' : 'i'}` : `${age} giorn${age === 1 ? 'o' : 'i'}`
            });
        } else {
            checks.push({
                status: 'unknown',
                label: 'Età Dominio',
                detail: 'Informazioni non disponibili'
            });
        }
        
        // Calcola totale (escludi unknown ed error)
        const checkable = checks.filter(c => c.status !== 'unknown' && c.status !== 'error');
        const passed = checks.filter(c => c.status === 'pass').length;
        const total = checkable.length;
        const errored = checks.filter(c => c.status === 'error').length;
        
        // Genera HTML
        const checklistHTML = checks.map(check => {
            const icon = check.status === 'pass' ? '✅' : 
                        check.status === 'fail' ? '❌' : 
                        check.status === 'warn' ? '⚠️' : 
                        check.status === 'error' ? '⚡' :
                        '❓';
            const statusClass = check.status === 'pass' ? 'check-pass' : 
                               check.status === 'fail' ? 'check-fail' : 
                               check.status === 'warn' ? 'check-warn' :
                               check.status === 'error' ? 'check-error' :
                               'check-unknown';
            
            return `
                <div class="security-check ${statusClass}">
                    <span class="check-icon">${icon}</span>
                    <div class="check-content">
                        <div class="check-label">${check.label}</div>
                        <div class="check-detail">${check.detail}</div>
                    </div>
                </div>
            `;
        }).join('');
        
        // Box errori servizi (se presenti)
        const errorBoxHTML = serviceErrors.length > 0 ? `
            <div class="service-errors">
                <div class="error-title">⚠️ Alcuni Servizi Non Disponibili</div>
                <ul class="error-list">
                    ${serviceErrors.map(err => `
                        <li><strong>${err.service}:</strong> ${err.error}</li>
                    `).join('')}
                </ul>
                <div class="error-tip">
                    💡 <strong>Consiglio:</strong> Riprova l'analisi tra qualche minuto o controlla la configurazione API.
                </div>
            </div>
        ` : '';
        
        return `
            <div class="security-checklist">
                <h3 class="checklist-title">🔒 Analisi Sicurezza</h3>
                ${checklistHTML}
                <div class="checklist-summary">
                    <strong>Risultato:</strong> ${passed}/${total} test superati
                    ${errored > 0 ? ` <span class="error-count">(${errored} errori)</span>` : ''}
                </div>
            </div>
            ${errorBoxHTML}
        `;
    }
    
    /**
     * Parsifica errori API per messaggi user-friendly
     */
    parseAPIError(message) {
        if (!message) return null;
        
        if (message.includes('quota') || message.includes('limit')) {
            return 'Quota API esaurita - riprova domani';
        }
        if (message.includes('timeout')) {
            return 'Timeout - servizio troppo lento';
        }
        if (message.includes('403') || message.includes('unauthorized')) {
            return 'API key non valida o scaduta';
        }
        if (message.includes('404')) {
            return 'Servizio API non trovato';
        }
        if (message.includes('500') || message.includes('502')) {
            return 'Errore server API - riprova più tardi';
        }
        
        return message.length > 60 ? message.substring(0, 60) + '...' : message;
    }
    
    buildContextualMessage(data, risk) {
        const issues = [];
        
        // Rileva problemi specifici
        const flags = risk.flags || [];
        
        // Hosting service
        if (flags.some(f => f.category === 'hosting_service' || f.message?.includes('ospitato'))) {
            issues.push('Ospitato su servizio cloud generico');
        }
        
        // URL structure
        if (flags.some(f => f.category === 'suspicious_url_structure' || f.message?.includes('codificat'))) {
            issues.push('Contiene dati codificati nell\'URL');
        }
        
        // Hash/params
        if (flags.some(f => ['suspicious_hash', 'suspicious_parameters'].includes(f.category))) {
            issues.push('Struttura URL non standard');
        }
        
        // Crypto
        if (flags.some(f => f.message?.toLowerCase().includes('crypto') || f.message?.toLowerCase().includes('coin'))) {
            issues.push('Keywords crypto/investment (scam comuni)');
        }
        
        // Typosquatting
        if (flags.some(f => f.category === 'typosquatting' || f.message?.includes('imitazione'))) {
            issues.push('Possibile imitazione di sito legittimo');
        }
        
        // Malware
        const vtData = data.results?.virustotal?.data;
        if (vtData && vtData.positives > 0) {
            issues.push(`Rilevato da ${vtData.positives} antivirus`);
        }
        
        // Se nessun issue specifico ma score alto
        if (issues.length === 0 && risk.score >= 40) {
            issues.push('Multipli segnali di rischio rilevati');
        }
        
        // Genera messaggio
        if (issues.length === 0 && risk.score < 20) {
            return `
                <div class="contextual-message message-safe">
                    <h3 class="message-title">✅ NESSUN PERICOLO EVIDENTE</h3>
                    <p class="message-text">
                        Non abbiamo rilevato segnali evidenti di pericolo. 
                        Tuttavia, verifica sempre la presenza di HTTPS e 
                        controlla recensioni indipendenti prima di inserire dati personali.
                    </p>
                </div>
            `;
        }
        
        const issueList = issues.map(i => `<li>${i}</li>`).join('');
        const severity = risk.score >= 60 ? 'danger' : risk.score >= 40 ? 'warning' : 'caution';
        const title = risk.score >= 60 ? '⛔ ATTENZIONE - ALTO RISCHIO' : 
                     risk.score >= 40 ? '⚠️ ATTENZIONE' : 
                     '⚡ CAUTELA';
        
        const explanation = flags.some(f => f.category === 'hosting_service')
            ? `<p class="message-explanation">
                Anche se il dominio principale può sembrare legittimo, 
                chiunque può caricare contenuti su servizi di hosting cloud. 
                Verifica sempre la fonte del link prima di procedere.
               </p>`
            : '';
        
        return `
            <div class="contextual-message message-${severity}">
                <h3 class="message-title">${title}</h3>
                <p class="message-text">
                    ${issues.length > 0 ? 'Questo link presenta segnali sospetti:' : ''}
                </p>
                ${issues.length > 0 ? `<ul class="message-issues">${issueList}</ul>` : ''}
                ${explanation}
            </div>
        `;
    }
    
    buildFlagsHTML(flags) {
        const flagsHTML = flags.map(flag => {
            const icon = this.getFlagIcon(flag.severity);
            const color = this.getSeverityColor(flag.severity);
            
            return `
                <div class="flag-item" style="border-left: 3px solid ${color};">
                    <div class="flag-icon" aria-hidden="true">${icon}</div>
                    <div class="flag-content">
                        <div class="flag-message">${this.escapeHTML(flag.message)}</div>
                        <div class="flag-source">Fonte: ${this.escapeHTML(flag.source)}</div>
                    </div>
                </div>
            `;
        }).join('');
        
        return `
            <div class="result-flags">
                <h3 class="flags-title">⚠️ Segnali Rilevati</h3>
                <div class="flags-summary">
                    ${flags.filter(f => f.severity === 'critical').length > 0 ? 
                        `<span class="summary-badge badge-critical">🔴 ${flags.filter(f => f.severity === 'critical').length} Critici</span>` : ''}
                    ${flags.filter(f => f.severity === 'high').length > 0 ? 
                        `<span class="summary-badge badge-high">🟠 ${flags.filter(f => f.severity === 'high').length} Alti</span>` : ''}
                    ${flags.filter(f => f.severity === 'medium').length > 0 ? 
                        `<span class="summary-badge badge-medium">🟡 ${flags.filter(f => f.severity === 'medium').length} Medi</span>` : ''}
                </div>
                <div class="flags-list">
                    ${flagsHTML}
                </div>
            </div>
        `;
    }
    
    buildRecommendationsHTML(recommendations) {
        const recsHTML = recommendations.map(rec => `
            <div class="recommendation-item">
                <div class="rec-icon" aria-hidden="true">${rec.icon}</div>
                <div class="rec-content">
                    <div class="rec-title">${this.escapeHTML(rec.title)}</div>
                    <div class="rec-message">${this.escapeHTML(rec.message)}</div>
                </div>
            </div>
        `).join('');
        
        return `
            <div class="result-recommendations">
                <h3 class="recs-title">📋 Cosa Fare</h3>
                ${recsHTML}
            </div>
        `;
    }
    
    getRiskColor(level) {
        const colors = {
            minimal: '#16a34a',
            low: '#16a34a',
            medium: '#ea580c',
            high: '#dc2626',
            critical: '#dc2626'
        };
        return colors[level] || '#6b7280';
    }
    
    getRiskIcon(level) {
        const icons = {
            minimal: '✅',
            low: '✅',
            medium: '⚠️',
            high: '⛔',
            critical: '🚫'
        };
        return icons[level] || '❓';
    }
    
    getSeverityColor(severity) {
        const colors = {
            critical: '#dc2626',
            high: '#ea580c',
            medium: '#f59e0b',
            low: '#3b82f6'
        };
        return colors[severity] || '#6b7280';
    }
    
    getFlagIcon(severity) {
        const icons = {
            critical: '🔴',
            high: '🟠',
            medium: '🟡',
            low: '🔵'
        };
        return icons[severity] || '⚪';
    }
    
    escapeHTML(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
    
    showError(message) {
        this.resultsEl.innerHTML = `
            <div class="error-card">
                <div class="error-icon" aria-hidden="true">❌</div>
                <h3 class="error-title">Errore</h3>
                <p class="error-message">${this.escapeHTML(message)}</p>
                <p class="error-help">
                    Verifica che l'indirizzo sia corretto e riprova.
                    Se il problema persiste, invia una segnalazione.
                </p>
            </div>
        `;
        this.resultsEl.classList.remove('hidden');
        this.resultsEl.scrollIntoView({ behavior: 'smooth' });
    }
}

// ===================================
// GESTIONE FEEDBACK
// ===================================

class FeedbackManager {
    constructor() {
        this.form = document.getElementById('feedback-form');
        this.statusEl = document.getElementById('feedback-status');
        
        this.init();
    }
    
    init() {
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.submitFeedback();
        });
    }
    
    async submitFeedback() {
        const formData = {
            type: document.getElementById('feedback-type').value,
            url: document.getElementById('feedback-url').value,
            message: document.getElementById('feedback-message').value,
            timestamp: new Date().toISOString(),
        };
        
        if (!formData.type || !formData.message) {
            this.showStatus('Per favore compila tutti i campi obbligatori.', 'error');
            return;
        }
        
        try {
            // Crea email mailto con i dati
            const subject = encodeURIComponent(`[Scam Detector] ${this.getTypeName(formData.type)}`);
            const body = this.buildEmailBody(formData);
            
            // Apri client email dell'utente
            window.location.href = `mailto:${CONFIG.FEEDBACK_EMAIL}?subject=${subject}&body=${body}`;
            
            // Mostra messaggio di successo
            this.showStatus(
                '✅ Il tuo programma di posta è stato aperto. Invia l\'email per completare la segnalazione. Grazie!',
                'success'
            );
            
            // Reset form dopo 3 secondi
            setTimeout(() => {
                this.form.reset();
                this.hideStatus();
            }, 5000);
            
        } catch (error) {
            this.showStatus(
                '❌ Errore nell\'apertura del client email. Prova a inviare manualmente una email.',
                'error'
            );
        }
    }
    
    buildEmailBody(formData) {
        let body = `Tipo di segnalazione: ${this.getTypeName(formData.type)}\n\n`;
        
        if (formData.url) {
            body += `Sito web: ${formData.url}\n\n`;
        }
        
        body += `Messaggio:\n${formData.message}\n\n`;
        
        body += `---\n`;
        body += `Data: ${new Date(formData.timestamp).toLocaleString('it-IT')}\n`;
        body += `User Agent: ${navigator.userAgent}`;
        
        return encodeURIComponent(body);
    }
    
    getTypeName(type) {
        const types = {
            'false-positive': 'Falso Positivo',
            'false-negative': 'Falso Negativo',
            'bug': 'Segnalazione Bug',
            'suggestion': 'Suggerimento',
            'other': 'Altro'
        };
        return types[type] || type;
    }
    
    showStatus(message, type) {
        this.statusEl.textContent = message;
        this.statusEl.className = `feedback-status ${type}`;
        this.statusEl.classList.remove('hidden');
        this.statusEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    
    hideStatus() {
        this.statusEl.classList.add('hidden');
    }
}

// ===================================
// STILI DINAMICI PER RISULTATI
// ===================================

function injectResultStyles() {
    const style = document.createElement('style');
    style.textContent = `
        .result-card {
            background: white;
            padding: 2rem;
            border-radius: 1rem;
            box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
            margin-bottom: 2rem;
        }
        
        .result-header {
            text-align: center;
            margin-bottom: 2rem;
        }
        
        .result-icon {
            margin-bottom: 1rem;
        }
        
        .result-title {
            font-size: 2.25rem;
            font-weight: 700;
            margin-bottom: 1rem;
            color: #111827;
        }
        
        .result-url {
            font-size: 1.125rem;
            color: #374151;
            word-break: break-all;
        }
        
        .result-score {
            margin: 2rem 0;
        }
        
        .score-label {
            font-size: 1.25rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            color: #111827;
        }
        
        .score-bar {
            background: #e5e7eb;
            border-radius: 9999px;
            height: 40px;
            overflow: hidden;
            position: relative;
        }
        
        .score-fill {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding: 0 1rem;
            transition: width 0.5s ease-out;
        }
        
        .score-value {
            color: white;
            font-size: 1.125rem;
            font-weight: 700;
        }
        
        .result-recommendation {
            padding: 1.5rem;
            border-radius: 0.75rem;
            margin: 2rem 0;
        }
        
        .recommendation-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            color: #111827;
        }
        
        .recommendation-text {
            font-size: 1.25rem;
            line-height: 1.7;
            color: #374151;
        }
        
        .result-flags {
            margin: 2rem 0;
        }
        
        .flags-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            color: #111827;
        }
        
        .flags-summary {
            display: flex;
            gap: 0.75rem;
            flex-wrap: wrap;
            margin-bottom: 1.5rem;
        }
        
        .summary-badge {
            display: inline-block;
            padding: 0.5rem 1rem;
            border-radius: 9999px;
            font-size: 1rem;
            font-weight: 600;
            background: white;
            border: 2px solid currentColor;
        }
        
        .badge-critical { color: #dc2626; }
        .badge-high { color: #ea580c; }
        .badge-medium { color: #f59e0b; }
        
        .flags-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
        
        .flag-item {
            display: flex;
            gap: 1rem;
            padding: 1rem;
            background: #f9fafb;
            border-radius: 0.75rem;
        }
        
        .flag-icon {
            font-size: 1.875rem;
            flex-shrink: 0;
        }
        
        .flag-content {
            flex: 1;
        }
        
        .flag-message {
            font-size: 1.125rem;
            font-weight: 500;
            margin-bottom: 0.25rem;
            color: #111827;
        }
        
        .flag-source {
            font-size: 1rem;
            color: #6b7280;
        }
        
        .result-recommendations {
            margin: 2rem 0;
        }
        
        .recs-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            color: #111827;
        }
        
        .recommendation-item {
            display: flex;
            gap: 1rem;
            padding: 1rem;
            background: #f0f9ff;
            border-radius: 0.75rem;
            margin-bottom: 1rem;
            border-left: 4px solid #3b82f6;
        }
        
        .rec-icon {
            font-size: 1.875rem;
            flex-shrink: 0;
        }
        
        .rec-content {
            flex: 1;
        }
        
        .rec-title {
            font-size: 1.125rem;
            font-weight: 600;
            margin-bottom: 0.25rem;
            color: #111827;
        }
        
        .rec-message {
            font-size: 1.125rem;
            color: #374151;
            line-height: 1.6;
        }
        
        .result-footer {
            margin-top: 2rem;
            padding-top: 1.5rem;
            border-top: 2px solid #e5e7eb;
            text-align: center;
        }
        
        .result-timestamp {
            font-size: 1rem;
            color: #6b7280;
            margin-bottom: 0.75rem;
        }
        
        .result-disclaimer {
            font-size: 1.125rem;
            font-weight: 500;
            color: #ea580c;
        }
        
        .error-card {
            background: #fee2e2;
            border: 3px solid #dc2626;
            border-radius: 1rem;
            padding: 2rem;
            text-align: center;
        }
        
        .error-icon {
            font-size: 4rem;
            margin-bottom: 1rem;
        }
        
        .error-title {
            font-size: 1.875rem;
            font-weight: 700;
            color: #dc2626;
            margin-bottom: 1rem;
        }
        
        .error-message {
            font-size: 1.25rem;
            color: #374151;
            margin-bottom: 1rem;
        }
        
        .error-help {
            font-size: 1.125rem;
            color: #6b7280;
        }
    `;
    document.head.appendChild(style);
}

// ===================================
// INIZIALIZZAZIONE
// ===================================

document.addEventListener('DOMContentLoaded', () => {
    // Inietta stili dinamici
    injectResultStyles();
    
    // Inizializza componenti
    new CookieManager();
    new DisclaimerManager();
    new URLAnalyzer();
    new FeedbackManager();
});
